/*
 * How a widget says a date or a period, wherever it is written: by the tenant that works a figure out and
 * by the app and a canvas that draw it. Months are written out here and not asked of the runtime, whose
 * locale data differs between a browser, Node and Bun ("Sept" and "Sep", "10 Oct" and "10 Oct,").
 *
 * A day is a calendar day held as its UTC midnight, as the tenant holds the workspace's wall clock, so it
 * is read back in UTC.
 */
export const MONTH_NAMES = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
/** As British English shortens them: September keeps its fourth letter. */
export const SHORT_MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sept", "Oct", "Nov", "Dec"];
const DAY = 86_400_000;
const month = (day, short) => (short ? SHORT_MONTH_NAMES : MONTH_NAMES)[day.getUTCMonth()];
/** A day in words: "7 October 2026", or short, "7 Oct 2026". */
export function dayWords(day, form = "long") {
    return `${day.getUTCDate()} ${month(day, form === "short")} ${day.getUTCFullYear()}`;
}
/** What a period on a chart's scale is called, from the day it starts on: "7 Oct", "W1 Oct", "Oct 26", "2026". */
export function periodScaleWords(start, bucket) {
    if (bucket === "year")
        return String(start.getUTCFullYear());
    if (bucket === "day")
        return `${start.getUTCDate()} ${month(start, true)}`;
    if (bucket === "week")
        return `W${Math.ceil(start.getUTCDate() / 7)} ${month(start, true)}`;
    return `${month(start, true)} ${String(start.getUTCFullYear()).slice(-2)}`;
}
const SCALE_MONTH = SHORT_MONTH_NAMES.join("|");
const SCALE_FORMS = [
    [new RegExp(`^(${SCALE_MONTH}) (\\d{2})$`, "u"), (short, year) => `${fullMonth(short)} 20${year}`],
    [new RegExp(`^(\\d{1,2}) (${SCALE_MONTH})$`, "u"), (date, short) => `${date} ${fullMonth(short)}`],
    [new RegExp(`^W(\\d) (${SCALE_MONTH})$`, "u"), (week, short) => `Week ${week} of ${fullMonth(short)}`],
];
const fullMonth = (short) => MONTH_NAMES[SHORT_MONTH_NAMES.indexOf(short)];
/**
 * A period as a chart's scale names it, written out: "Oct 26" is "October 2026", "7 Oct" is "7 October" and
 * "W1 Oct" is "Week 1 of October". A scale has little room and a reader's eye beside it; said on its own, as
 * under a pointer, "Oct 26" reads as a day as easily as a month. Anything else (a year, a category's name)
 * is given back as it is.
 */
export function periodInFull(scaleWords) {
    for (const [form, written] of SCALE_FORMS) {
        const parts = form.exec(scaleWords);
        if (parts)
            return written(...parts.slice(1));
    }
    return scaleWords;
}
const firstOfMonth = (day) => day.getUTCDate() === 1;
const lastOfMonth = (day) => new Date(day.getTime() + DAY).getUTCDate() === 1;
/**
 * A period from its first day to its last, in the fewest words that say it. Whole months and years are
 * named as what they are: "October 2026", "July–September 2026", "November 2025 – February 2026", "2026",
 * "2024–2026". Anything else is its days: "7 October 2026", "13–17 October 2026",
 * "13 September – 4 October 2026", "13 December 2025 – 4 January 2026". A dash joins two numbers or two
 * names closely, and stands apart where either side is more than one word.
 */
export function periodWords(first, last) {
    const [from, to] = first.getTime() <= last.getTime() ? [first, last] : [last, first];
    const [fromYear, toYear] = [from.getUTCFullYear(), to.getUTCFullYear()];
    if (firstOfMonth(from) && lastOfMonth(to)) {
        const whole = from.getUTCMonth() === 0 && to.getUTCMonth() === 11;
        if (whole)
            return fromYear === toYear ? String(fromYear) : `${fromYear}–${toYear}`;
        if (fromYear !== toYear)
            return `${month(from, false)} ${fromYear} – ${month(to, false)} ${toYear}`;
        return from.getUTCMonth() === to.getUTCMonth() ? `${month(from, false)} ${fromYear}` : `${month(from, false)}–${month(to, false)} ${fromYear}`;
    }
    if (fromYear !== toYear)
        return `${dayWords(from)} – ${dayWords(to)}`;
    if (from.getUTCMonth() !== to.getUTCMonth())
        return `${from.getUTCDate()} ${month(from, false)} – ${dayWords(to)}`;
    return from.getUTCDate() === to.getUTCDate() ? dayWords(from) : `${from.getUTCDate()}–${dayWords(to)}`;
}
